import React, {useState} from "react";
import classNames from "classnames";
import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import {useAppState} from "@stores/AppStateContext";

import {ModalConfirm} from "../../../components/ModalConfirm"
import {Button} from "../../../components/Button";

import {MODALS} from "../modal-names";
import {MONTHS} from "../../../../utils/date";
import {screenSizeProvider} from "../../../../stores/ScreenSizeProvider";

import "./YearCalendar.css"

export interface MonthDay {
    month: number;
    day: number;
}

export interface YearCalendarModalProps {
    getSelectedDates: () => MonthDay[];
    onConfirm: (dates: MonthDay[]) => Promise<void>;
}

export const YearCalendar = observer((params: YearCalendarModalProps) => {
    const appState = useAppState();
    const initialSelected = params.getSelectedDates();

    const [localSelectedDates, setLocalSelectedDates] = useState<MonthDay[]>(initialSelected);
    const isMobile = screenSizeProvider.view !== 'desktop';

    const toggleDay = (month: number, day: number) => {
        const isSelected = localSelectedDates.some(d => d.month === month && d.day === day);
        const updated = isSelected
            ? localSelectedDates.filter(d => !(d.month === month && d.day === day))
            : [...localSelectedDates, {month, day}];
        setLocalSelectedDates(updated);
    };
    const handleClose = () => {
        appState.setModal(null);
    };

    const getDaysInMonth = (month: number, year: number) =>
        new Date(year, month + 1, 0).getDate();

    const currentYear = new Date().getFullYear();
    const activeModal = appState.topModal;

    return (
        <ModalConfirm
            contentClassName={"YearCalendarModal"}
            maxWidth={1128}
            header={intl.get('YearCalendar.header').d('Select Dates')}
            open={activeModal === MODALS.YEAR_CALENDAR}
            onCancel={() => handleClose()}
            onClose={() => handleClose()}
            onConfirm={async () => {
                await params.onConfirm(localSelectedDates);
                handleClose();
            }}
        >
            <div>
                <Button
                    text={intl.get("Calendar-Repeat.Month.button").d("Clear All")}
                    type="primary"
                    style="inline"
                    size="sm"
                    onClick={() => {
                        setLocalSelectedDates([]);
                    }}
                />
            </div>
            <div className="YearCalendar">
                {MONTHS.map((monthName, monthIndex) => {
                    const daysInMonth = getDaysInMonth(monthIndex, currentYear);

                    return (
                        <div key={monthName} className="YearCalendar__MonthBlock">
                            <div className="label-large-bold YearCalendar__MonthName">{monthName}</div>
                            <div className="YearCalendar__DaysGrid">
                                {Array.from({length: daysInMonth}, (_, i) => {
                                    const day = i + 1;
                                    const isActive = localSelectedDates.some(
                                        d => d.month === monthIndex && d.day === day
                                    );

                                    return (
                                        <div
                                            key={`${monthIndex}-${day}`}
                                            className={classNames({
                                                "YearCalendar__DayCell": true,
                                                "label-small": !isMobile,
                                                "body-medium": isMobile,
                                                "YearCalendar__DayCell--active": isActive,
                                            })}
                                            onClick={() => toggleDay(monthIndex, day)}
                                        >
                                            {day}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </ModalConfirm>
    );
});
